// ReportPreview.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import Handlebars from "handlebars";
import styles from "./ReportPreview.module.css";
import { useNavigate } from "react-router-dom";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - payload: object (may include archetype_image as string path)
 * - template: string (Handlebars template)
 */
export default function ReportPreviewFree({
  open,
  onClose,
  payload = {},
  template,
}) {
  const navigate = useNavigate();
  const [url, setUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const iframeRef = useRef(null);
  const prevUrlRef = useRef(null);

  const defaultTemplate = template || FREE_TEMPLATE;

  // stable string version of payload for equality checks
  const payloadStr = useMemo(() => {
    try {
      return JSON.stringify(payload);
    } catch (e) {
      return String(payload);
    }
  }, [payload]);

  // helper: fetch a resource and convert to data URL (base64)
  async function fetchAsDataUrl(url) {
    try {
      const resp = await fetch(url, { cache: "force-cache", mode: "cors" });
      if (!resp.ok) throw new Error("fetch failed: " + resp.status);
      const blob = await resp.blob();
      return await blobToDataURL(blob);
    } catch (err) {
      console.warn("fetchAsDataUrl failed for", url, err);
      throw err;
    }
  }

  function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  useEffect(() => {
    if (!open) {
      // cleanup when closed
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }
      setUrl(null);
      return;
    }

    let mounted = true;
    setProcessing(true);

    (async () => {
      try {
        // clone payload so we can safely modify image field
        const cloned = payload ? JSON.parse(JSON.stringify(payload)) : {};

        // --- maskPremiumForFree: reduz conteúdo premium para preview gratuito ---
        function maskPremiumForFree(clone) {
          if (!clone) return clone;

          // sinaliza que é versão preview
          clone._isPreview = true;
          clone._premiumTeasers = clone._premiumTeasers || {};

          // 1) Plano mensal: remover semanas completas, deixar miniPlan/teaser
          if (clone.monthlyPlan) {
            // keep a short "miniPlan" if exists, otherwise create a teaser
            clone._premiumTeasers.monthlyPlan = true;
            // preserve a tiny preview if available
            clone.miniPlan = clone.miniPlan || [
              {
                title: "Semana 1 (amostra)",
                text: "Configurar valor e hábito âncora (ex.: 2–5 min/dia).",
              },
              {
                title: "Semana 2 (amostra)",
                text: "Experimentar micro-hábitos e registrar aprendizados.",
              },
            ];
            // remove full months/weeks from cloned so template doesn't render them
            delete clone.monthlyPlan;
          }

          // 2) Exercícios ACT: manter só 1 exemplo (ou versão resumida)
          if (
            Array.isArray(clone.actExercises) &&
            clone.actExercises.length > 0
          ) {
            clone._premiumTeasers.exercises = true;
            clone.actExercises = clone.actExercises.slice(0, 1).map((ex, i) => {
              return {
                title: ex.title,
                description: ex.description,
                teaser: true,
              };
            });
          } else if (
            Array.isArray(clone.premiumPayload?.actExercises) &&
            clone.premiumPayload.actExercises.length
          ) {
            clone._premiumTeasers.exercises = true;
            clone.actExercises = clone.premiumPayload.actExercises.slice(0, 1);
          }

          // 3) Quadrinho/metáfora: manter apenas cover thumb; bloquear páginas completas
          if (clone.comicPages) {
            clone._premiumTeasers.comic = true;
            // keep only the cover (if exist) and remove pages so modal can't open full comic
            clone.coverImage =
              clone.coverImage ||
              (Array.isArray(clone.comicPages)
                ? clone.comicPages[0]
                : clone.coverImage);
            delete clone.comicPages;
            if (clone.premiumPayload && clone.premiumPayload.comicPages)
              delete clone.premiumPayload.comicPages;
          }

          // 4) Relationships/Growth sections — keep short summaries / first N items
          if (
            Array.isArray(clone.relationships) &&
            clone.relationships.length > 3
          ) {
            clone._premiumTeasers.relationships = true;
            clone.relationships = clone.relationships.slice(0, 3); // trim
          }
          if (Array.isArray(clone.growth) && clone.growth.length > 3) {
            clone._premiumTeasers.growth = true;
            clone.growth = clone.growth.slice(0, 3);
          }

          // 5) Strengths / recommendations truncation
          if (
            Array.isArray(clone.temperamentStrengths) &&
            clone.temperamentStrengths.length > 3
          ) {
            clone.temperamentStrengths = clone.temperamentStrengths.slice(0, 3);
            clone._premiumTeasers.temperamentStrengths = true;
          }
          if (
            Array.isArray(clone.recommendations) &&
            clone.recommendations.length > 3
          ) {
            clone.recommendations = clone.recommendations.slice(0, 3);
            clone._premiumTeasers.recommendations = true;
          }

          // 6) add short CTA (template may render this)
          clone._upgradeCTA = clone._upgradeCTA || {
            title: "Relatório Premium",
            text: "Desbloqueie o plano mensal completo, exercícios e o quadrinho completo.",
            href: "/purchase",
          };

          return clone;
        }

        // ---- FORÇAR conversão para data URL sempre que possível ----
        const imgVal = cloned.archetype_image;
        if (
          imgVal &&
          typeof imgVal === "string" &&
          !imgVal.startsWith("data:")
        ) {
          let converted = null;
          // Try direct fetch (works for absolute, root-relative, or relative if accessible)
          try {
            converted = await fetchAsDataUrl(imgVal);
          } catch (err) {
            // try resolving relative against origin (strip ../)
            try {
              const guess =
                window.location.origin +
                (imgVal.startsWith("/")
                  ? imgVal
                  : "/" + imgVal.replace(/^(\.\.\/)+/, ""));
              converted = await fetchAsDataUrl(guess);
              console.log(
                "ReportPreview: converted archetype_image (via origin guess):",
                guess
              );
            } catch (err2) {
              // Try if payload value could already be a bundler path like /static/media/xyz.png
              try {
                if (!imgVal.startsWith("/")) {
                  const alt = window.location.origin + "/" + imgVal;
                  converted = await fetchAsDataUrl(alt);
                  console.log(
                    "ReportPreview: converted archetype_image (via alt guess):",
                    alt
                  );
                }
              } catch (err3) {
                // give up
                console.warn(
                  "ReportPreview: could not fetch/convert archetype_image:",
                  imgVal
                );
                converted = null;
              }
            }
          }
          // set converted or blank (so template fallback applies)
          cloned.archetype_image = converted || "";
        }

        // compile the template
        const tpl = Handlebars.compile(defaultTemplate);
        const finalPayload = true ? maskPremiumForFree(cloned) : cloned;
        const html = tpl(finalPayload || {});

        const blob = new Blob([html], { type: "text/html" });
        const blobUrl = URL.createObjectURL(blob);

        // revoke previous
        if (prevUrlRef.current && prevUrlRef.current !== blobUrl) {
          URL.revokeObjectURL(prevUrlRef.current);
        }
        prevUrlRef.current = blobUrl;

        if (!mounted) return;
        setUrl(blobUrl);
      } catch (err) {
        console.error("ReportPreview: error generating preview", err);
        if (mounted) setUrl(null);
      } finally {
        if (mounted) setProcessing(false);
      }
    })();

    return () => {
      mounted = false;
      // keep prevUrlRef until closed to avoid flicker
    };
    // depend on open and serialized payload
  }, [open, payloadStr, template, defaultTemplate]);

  useEffect(() => {
    return () => {
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }
    };
  }, []);

  function handlePrint() {
    if (!iframeRef.current) return;
    iframeRef.current.contentWindow.focus();
    iframeRef.current.contentWindow.print();
  }

  function handleDownloadHtml() {
    if (!prevUrlRef.current) return;
    const a = document.createElement("a");
    a.href = prevUrlRef.current;
    a.download = `${(payload?.name || "report").replace(/\s+/g, "_")}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Preview do relatório"
      >
        <header className={styles.header}>
          <div>
            <strong>{payload?.name || "Relatório"}</strong>
            <div className={styles.smallMuted}>
              {payload?.archetype_label || payload?.personalityType}
            </div>
          </div>

          <div className={styles.actions}>
            {/* <button
              className={styles.btn}
              // onClick={handleDownloadHtml}
              onClick={() => navigate("/premium-purchase")}
              disabled={processing}
            >
              Comprar relatório premium
            </button> */}
            <button
              className={styles.btnPrimary}
              onClick={handlePrint}
              disabled={!url || processing}
            >
              Imprimir / Salvar como PDF
            </button>
            <button
              className={styles.close}
              onClick={onClose}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        </header>

        {/* dentro do return do ReportPreview, substitua frameWrap contents por isto */}
        <div className={styles.frameWrap}>
          {processing ? (
            <div className={styles.loading}>Preparando visualização…</div>
          ) : url ? (
            <iframe
              ref={iframeRef}
              title="Report preview"
              src={url}
              className={styles.iframe}
            />
          ) : (
            // Fallback legível quando iframe não estiver disponível
            <div style={{ padding: 18 }}>
              <div style={{ marginBottom: 12 }}>
                <strong style={{ fontSize: 18 }}>
                  {payload?.name || "Relatório"}
                </strong>
                <div style={{ color: "#6b7280", marginTop: 4 }}>
                  {payload?.archetype_label || payload?.personalityType}
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <em>
                  {payload?.summary_executive || "Resumo não disponível."}
                </em>
              </div>

              {payload?.temperamentStrengths && (
                <div style={{ marginBottom: 12 }}>
                  <strong>Pontos naturais</strong>
                  <ul>
                    {payload.temperamentStrengths.slice(0, 6).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}

              {payload?.miniPlan && payload.miniPlan.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <strong>Mini-plano</strong>
                  <ol>
                    {payload.miniPlan.map((p, i) => (
                      <li key={i}>
                        {typeof p === "string" ? (
                          p
                        ) : (
                          <>
                            <strong>{p.title || `Dia ${i + 1}`}</strong>
                            {p.text ? ` — ${p.text}` : ""}
                          </>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>
                Pré-visualização (fallback). Verifique o console para logs de
                debug (ReportPreview).
              </div>
            </div>
          )}
        </div>

        <footer className={styles.footer}>
          <small className={styles.smallMuted}>
            Esta é uma pré-visualização do relatório. Impressão usa as
            configurações do navegador.
          </small>
        </footer>
      </div>
    </div>
  );
}

/* minimal default template (substitua pelo template completo se quiser) */
const FREE_TEMPLATE = `
<!-- FREE_TEMPLATE: Preview do Relatório Premium (versão gratuita) -->
<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Pré-visualização — Relatório</title>

<style>
  :root{
    --bg:#fbfcfe; --card:#ffffff; --muted:#6b7280; --accent:#2e7dff;
    --maxw:900px;
  }
  body{font-family:Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; background:var(--bg); color:#111;margin:0;padding:20px;display:flex;justify-content:center}
  .wrap{width:100%;max-width:var(--maxw);}
  .cover{display:flex;gap:16px;background:var(--card);padding:18px;border-radius:12px;box-shadow:0 8px 30px rgba(13,17,24,0.05);align-items:center}
  .cover img{width:140px;height:196px;object-fit:cover;border-radius:8px;border:1px solid #eef2ff}
  .meta{flex:1}
  .clan{font-size:0.9rem;color:var(--muted);margin-bottom:6px}
  h1{margin:0;font-size:1.15rem}
  .summary{margin-top:10px;color:#222;line-height:1.45}
  .section{background:var(--card);margin-top:14px;padding:14px;border-radius:10px}
  .section h3{margin:0 0 8px 0;font-size:1rem}
  .mini-list{padding-left:18px;color:#222}
  .cta{display:inline-block;margin-top:10px;background:var(--accent);color:white;padding:8px 12px;border-radius:8px;text-decoration:none}
  .teaser{margin-top:8px;padding:10px;border-radius:8px;background:linear-gradient(90deg,#fff8e6,#fff);border:1px solid #fff1d6;color:#6b3f00}
  .ex-list{margin:0;padding-left:18px}
  .muted{color:var(--muted);font-size:0.95rem}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  @media (max-width:720px){ .grid{grid-template-columns:1fr} .cover{flex-direction:column;align-items:flex-start} .cover img{width:100%;height:auto} }
</style>
</head>
<body>
  <div class="wrap">

    <!-- COVER / HEADER -->
    <div class="cover" role="banner">
      {{#if archetype_image}}
        <img src="{{archetype_image}}" alt="Capa: {{archetype_label}}">
      {{else}}
        <img src="{{coverImage}}" alt="Capa">
      {{/if}}
      <div class="meta">
        <div class="clan">Clã: {{archetype_clan}}</div>
        <h1>{{archetype_label}} — Perfil (pré-visualização)</h1>
        <div class="muted">Temperamento: {{temperamentSummary}}</div>
        <p class="summary">{{summaryShort}}</p>
        {{#if _isPreview}}
          <a class="cta" href="{{_upgradeCTA.href}}">{{_upgradeCTA.title}}</a>
        {{/if}}
      </div>
    </div>

    <!-- EXECUTIVE / HIGHLIGHTS -->
    <div class="section">
      <h3>Principais insights</h3>
      <div class="grid">
        <div>
          <strong>Forças</strong>
          <ul class="mini-list">
            {{#each temperamentStrengths}}
              <li>{{this}}</li>
            {{/each}}
          </ul>
        </div>

        <div>
          <strong>Desafios (resumido)</strong>
          <ul class="mini-list">
            {{#each temperamentChallenges}}
              <li>{{this}}</li>
            {{/each}}
          </ul>
        </div>
      </div>

      {{#if _premiumTeasers.recommendations}}
        <div class="teaser">Versão completa inclui recomendações práticas detalhadas para seu perfil.</div>
      {{/if}}
    </div>

    <!-- MONTHLY PLAN (teaser/mini) -->
    <div class="section" aria-labelledby="plan-title">
      <h3 id="plan-title">Planejamento Mensal — Amostra</h3>

      {{#if miniPlan}}
        <ol class="mini-list">
          {{#each miniPlan}}
            <li><strong>{{title}}</strong> — {{text}}</li>
          {{/each}}
        </ol>
        {{#if _premiumTeasers.monthlyPlan}}
          <div class="teaser">Plano mensal completo (4 semanas com atividades e ACT) disponível apenas no Premium.</div>
        {{/if}}
      {{else}}
        <p class="muted">Amostra do plano não disponível.</p>
      {{/if}}
    </div>

    <!-- ACT EXERCISES -->
    <div class="section">
      <h3>Exercícios ACT (exemplo)</h3>

      {{#if actExercises}}
        <ol class="ex-list">
          {{#each actExercises}}
            <li><strong>{{title}}</strong> — {{description}}</li>
          {{/each}}
        </ol>
        {{#if _premiumTeasers.exercises}}
          <div class="teaser">Mais exercícios detalhados e sequenciados disponíveis no relatório Premium.</div>
        {{/if}}
      {{else}}
        <p class="muted">Nenhum exercício disponível nesta pré-visualização.</p>
      {{/if}}
    </div>

    <!-- METAPHOR / COMIC COVER (no preview apenas a capa) -->
    <div class="section">
      <h3>Metáfora visual</h3>
      <p><strong>{{insights.archetype.metaphor}}</strong></p>

      {{#if coverImage}}
        <img src="{{coverImage}}" alt="Capa da metáfora" style="width:140px;height:auto;border-radius:8px;border:1px solid #eee;display:block;margin-top:8px"/>
      {{/if}}

      {{#if _premiumTeasers.comic}}
        <div class="teaser">Quadrinho completo (várias páginas) disponível apenas no Premium. <a href="{{_upgradeCTA.href}}">Desbloquear agora</a></div>
      {{/if}}
    </div>

    <!-- FOOTER / CTA -->
    <div style="margin-top:16px;text-align:center;color:var(--muted);font-size:0.95rem">
      {{#if _isPreview}}
        <div>Esta é uma pré-visualização. Para receber o relatório completo (PDF/JSX/Personalizado), clique no botão abaixo.</div>
        <div style="margin-top:10px"><a class="cta" href="{{_upgradeCTA.href}}">{{_upgradeCTA.title}}</a></div>
      {{else}}
        <div>Relatório gerado — ver versão completa no painel.</div>
      {{/if}}
    </div>

  </div>
</body>
</html>
`;
